/**
 * Pay-it-Forward: take a Crowdsignal survey without leaving the site.
 *
 * Survey buttons are plain new-tab links (so they still work without JS);
 * a button with data-survey-embed opens that survey in an iframe window
 * instead. Both URLs carry the website tag (config/crowdsignal/config.json
 * websiteTag), which is how the dashboard counts website responses.
 *
 * Crowdsignal's iframe view (?iframe=1) posts its content height to the
 * parent as { sentinel: 'amp', type: 'embed-size', height }; the frame is
 * sized to match so the window scrolls as one page. Without those messages
 * the frame simply fills the window and scrolls itself.
 *
 * Bundled into resources.js (every file in this folder is), so it no-ops on
 * other resources pages.
 */

function initSurveyWindow() {
    const win = document.getElementById('survey-window');
    if (!win) return;

    const titleEl = document.getElementById('survey-window-title');
    const tabLink = document.getElementById('survey-window-tab-link');
    const closeBtn = document.getElementById('survey-window-close');
    const body = document.getElementById('survey-window-body');
    const loading = document.getElementById('survey-window-loading');
    const confirmRow = document.getElementById('survey-window-confirm');
    const keepBtn = document.getElementById('survey-window-keep');
    const leaveBtn = document.getElementById('survey-window-leave');

    let frame = null;
    let frameOrigin = null;
    let opener = null;
    let loads = 0;

    const track = (event, data) => {
        try { window.umami?.track(event, data); } catch { /* analytics is optional */ }
    };

    function open(btn) {
        const embedUrl = btn.dataset.surveyEmbed;
        let origin;
        try { origin = new URL(embedUrl).origin; } catch { return false; }

        opener = btn;
        frameOrigin = origin;
        loads = 0;
        titleEl.textContent = btn.dataset.surveyTitle || btn.textContent.trim();
        tabLink.href = btn.href;
        hideConfirm();
        loading.classList.remove('is-hidden');

        frame = document.createElement('iframe');
        frame.className = 'survey-window__frame';
        frame.title = `Survey: ${titleEl.textContent}`;
        frame.src = embedUrl;
        frame.addEventListener('load', onFrameLoad);
        body.appendChild(frame);

        win.classList.remove('is-hidden');
        document.body.classList.add('survey-window-open');
        closeBtn.focus();
        track('survey-opened', { survey: titleEl.textContent, view: 'window' });
        return true;
    }

    function onFrameLoad() {
        loads += 1;
        loading.classList.add('is-hidden');
        // Each new survey page loads the frame again; start it at the top.
        if (loads > 1) body.scrollTop = 0;
    }

    function close() {
        hideConfirm();
        win.classList.add('is-hidden');
        document.body.classList.remove('survey-window-open');
        if (frame) {
            frame.removeEventListener('load', onFrameLoad);
            frame.remove();
            frame = null;
        }
        frameOrigin = null;
        opener?.focus();
        opener = null;
    }

    function askToClose() {
        confirmRow.classList.remove('is-hidden');
        keepBtn.focus();
    }

    function hideConfirm() {
        confirmRow.classList.add('is-hidden');
    }

    document.addEventListener('click', (e) => {
        const btn = e.target.closest('a[data-survey-embed]');
        if (!btn) return;
        // Let ctrl/cmd/shift/middle-click open the new tab as usual.
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            track('survey-opened', { survey: btn.dataset.surveyTitle || btn.textContent.trim(), view: 'tab' });
            return;
        }
        if (open(btn)) e.preventDefault();
    });

    document.querySelectorAll('.survey-card__btn:not([data-survey-embed])').forEach(btn => {
        btn.addEventListener('click', () => track('survey-opened', { survey: btn.textContent.trim(), view: 'tab' }));
    });

    tabLink.addEventListener('click', () => track('survey-opened', { survey: titleEl.textContent, view: 'tab' }));
    closeBtn.addEventListener('click', askToClose);
    keepBtn.addEventListener('click', () => { hideConfirm(); closeBtn.focus(); });
    leaveBtn.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || win.classList.contains('is-hidden')) return;
        e.preventDefault();
        if (confirmRow.classList.contains('is-hidden')) askToClose();
        else { hideConfirm(); closeBtn.focus(); }
    });

    // Keep keyboard focus inside the window while it's open.
    document.addEventListener('focusin', (e) => {
        if (win.classList.contains('is-hidden') || win.contains(e.target)) return;
        closeBtn.focus();
    });

    window.addEventListener('message', (e) => {
        if (!frame || e.origin !== frameOrigin || e.source !== frame.contentWindow) return;
        const d = e.data;
        if (!d || d.sentinel !== 'amp' || d.type !== 'embed-size') return;
        const h = Number(d.height);
        if (!Number.isFinite(h) || h <= 0) return;
        frame.style.height = `${Math.max(Math.ceil(h), body.clientHeight)}px`;
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSurveyWindow);
} else {
    initSurveyWindow();
}
